import { FC } from 'react';
import { CurrentUser, DataRoomEmail, DirectConnectMilestoneReport, MilestoneReport } from '../../shared/models';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroMultiDropdown,
	CerebroMultiDropdownMenuItem,
	CerebroPrimaryButton,
	CerebroRichText,
	CerebroStyledBox,
	CerebroText,
} from '../../shared/components';
import { Grid } from '@mui/material';
import { DateUtils, FormikUtils } from '../../shared/utils';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { useApi, useAuth } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

export interface ISendLenderEmailsProps {
	milestones: (MilestoneReport | DirectConnectMilestoneReport)[];
	dataRoomGuid: string | undefined;
	open: boolean;
	statusFilter: 'active' | 'inactive' | 'all'
	close: () => void;
}

export const SendLenderEmailsForm: FC<ISendLenderEmailsProps> = ({ milestones, dataRoomGuid, open, statusFilter, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const { currentUser } = useAuth();

	const uniqueMilestones = Array.from(
		new Map(milestones.map(m => [m.emailAddress, m])).values()
	);

	const filteredMilestones = uniqueMilestones.filter(report => {
		if (statusFilter === 'all') return true;
		return report.dealBankStatus?.toLowerCase() === statusFilter;
	});

	const sendEmailMutation = useMutation(
		async ({ values }: SendEmailFormMutationVariables) => {
			const { data, error } = await getApiResponseData<DataRoomEmail>(
				api.post(CerebroApiEndpoints.DataRoomUsers.EmailMany(dataRoomGuid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Email was sent.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading>Email Lenders</CerebroHeading>

			<Formik
				validationSchema={SendEmailFormSchema}
				initialValues={{
					to: [] as string[],
					subject: '',
					emailBody: buildDefaultEmailBody(currentUser),
				}}
				onSubmit={FormikUtils.MutateOnSubmit(sendEmailMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ marginTop: 3, paddingRight: 3 }}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel>To</CerebroLabel>
									<Field
										id="to"
										as={CerebroMultiDropdown}
										name="to"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('to', errors, touched)}
									>
										{filteredMilestones.map(report => (
											<CerebroMultiDropdownMenuItem key={report.guid} value={report.emailAddress}>
												{report.firstName} {report.lastName} ({report.bankName}){' '}
												{DateUtils.FormatStringAsShortDate(report.lastAccessedOn?.toString())}
												{report.dealBankStatus === 'inactive' && (
													<> - {report.dealBankStatus}</>
												)}
											</CerebroMultiDropdownMenuItem>
										))}
									</Field>
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="subject" required>
										Subject
									</CerebroLabel>
									<Field
										id="subject"
										as={CerebroInput}
										name="subject"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('subject', errors, touched)}
									/>
									{FormikUtils.IsInvalid('subject', errors, touched) && (
										<CerebroText variant="error">{errors.subject}</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="emailBody" required>
										Email Body
									</CerebroLabel>
									<CerebroRichText
										value={values.emailBody ?? ''}
										onChange={updatedValue => setFieldValue('emailBody', updatedValue)}
									/>
									{FormikUtils.IsInvalid('emailBody', errors, touched) && (
										<CerebroText variant="error">{errors.emailBody}</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroStyledBox alignitems="left">
										<CerebroHeading
											size="h2"
											component="h2"
											sx={{ marginTop: -1, marginBottom: 2 }}
										>
											Legend
										</CerebroHeading>
										<CerebroText>#bankerFirstName</CerebroText>
										<CerebroText>#bankerLastName</CerebroText>
										<CerebroText sx={{ marginTop: 2 }}>
											Use these tags in the email body which will be replaced when the email is
											sent.
										</CerebroText>
									</CerebroStyledBox>
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Send Email
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

function buildDefaultEmailBody(currentUser: CurrentUser): string {
	let body: string = '<p>#bankerFirstName,</p><p><br/><br/></p>';

	body += '<p>Thank you</p>';
	body += `<p>${currentUser.firstName} ${currentUser.lastName}</p>`;

	return body;
}

type SendEmailFormMutationVariables = FormikUtils.MutateOnSubmitVariables<SendEmailFormValues>;
interface SendEmailFormValues {
	to: string[];
	subject: string;
	emailBody: string;
}

const SendEmailFormSchema = Yup.object().shape({
	subject: Yup.string().required('subject is required'),
	emailBody: Yup.string().required('email body is required'),
});
